// Port of ios/Sources/ViewModels/ScheduleOptions.swift.
//
// WHEN a message can be scheduled for, without asking anyone to operate a calendar.
//
// Almost every scheduled message is one of three thoughts: "not right now", "this evening", and
// "first thing tomorrow". A picker answers all three badly — four taps and a decision about a
// date — so the picker is the LAST option here, not the first.
//
// Pure and fully parameterised on `now` and the zone, like `RelativeTime` and `MessageTimestamp`
// beside it: "first thing tomorrow" crosses a day boundary, a weekend and, twice a year, an hour
// that does not exist. The LABELS live in `Strings`, not here. A decision-for-decision twin of
// Android's `ScheduleOptions`, tests included.
import { locale as uiLocale } from '../i18n/i18n';
import { calendarDaysBetween, civil, dateFormatter } from './time';
import { addingDays, epochForCivil } from './zonedTime';

/**
 * Below this, "scheduled" is just "sent" — and it is inside the clock skew between this
 * device and the server.
 */
export const minimumLeadMillis = 60_000;

/** The working day this workspace keeps. */
export const morningHour = 9;
export const eveningHour = 18;

/** An evening option is only worth offering if it is comfortably ahead of the hour option. */
const eveningMinimumLeadMillis = 60 * 60_000;

/**
 * - `inAnHour`: round hour-ish from now — the "not right now" answer.
 * - `thisEvening`: today at `eveningHour`. Absent once the evening is here or nearly here.
 * - `tomorrowMorning`: tomorrow at `morningHour`. Absent when tomorrow is a Saturday or a Sunday.
 * - `nextMondayMorning`: the coming Monday at `morningHour` — what "first thing" means on a Friday.
 */
export type Kind = 'inAnHour' | 'thisEvening' | 'tomorrowMorning' | 'nextMondayMorning';

export interface Option {
  kind: Kind;
  atMillis: number;
}

/** Why an instant cannot be used. Null means it can. */
export type Problem = 'inThePast' | 'tooSoon';

export function problem(atMillis: number, now: number): Problem | null {
  if (atMillis <= now) return 'inThePast';
  if (atMillis - now < minimumLeadMillis) return 'tooSoon';
  return null;
}

/**
 * The offered instants, earliest first, all strictly in the future.
 *
 * Exactly ONE of `tomorrowMorning` / `nextMondayMorning` is ever present: they are the same
 * thought — the next working morning — and showing both would make the reader compare two
 * dates to answer "first thing".
 */
export function options(now: number, timeZone?: string): Option[] {
  const result: Option[] = [{ kind: 'inAnHour', atMillis: inAnHour(now) }];

  const evening = todayAt(eveningHour, now, timeZone);
  if (evening - now >= eveningMinimumLeadMillis) {
    result.push({ kind: 'thisEvening', atMillis: evening });
  }

  const working = nextWorkingMorning(now, timeZone);
  result.push({ kind: working.kind, atMillis: working.atMillis });

  return result.filter((o) => problem(o.atMillis, now) == null).sort((a, b) => a.atMillis - b.atMillis);
}

/**
 * THE SERVER'S OWN CADENCE, and the reason every time this file produces is a multiple of it.
 *
 * Mattermost does not post a scheduled message at its exact second: a background job sweeps
 * them on a five-minute boundary. Measured on this server, not assumed (2026-09-17): 07:39:39
 * went out at 07:40:00.111, and 07:41:50 waited until 07:45:00.109. A time ON the boundary is
 * honoured to a tenth of a second; a time just past one waits almost five minutes — so this
 * rounds UP to the boundary, because the app must not promise 09:03 for a message the server
 * will send at 09:05. See `.codex/satori-chat.md` for the full table.
 */
export const serverSweepMillis = 5 * 60_000;

/** The first instant at or after `atMillis` that the server's sweep will actually pick up. */
export function atServerSweep(atMillis: number): number {
  return Math.floor((atMillis + serverSweepMillis - 1) / serverSweepMillis) * serverSweepMillis;
}

/**
 * An hour from now, rounded UP to the server's next sweep: a scheduled time is something the
 * sender says out loud ("it goes out at ten to ten").
 */
export function inAnHour(now: number): number {
  return atServerSweep(now + 60 * 60_000);
}

/**
 * The next morning that is a working day — tomorrow, unless tomorrow is a weekend. Friday and
 * Saturday both answer Monday; Sunday answers tomorrow, which IS Monday.
 */
export function nextWorkingMorning(now: number, timeZone?: string): { kind: Kind; atMillis: number } {
  let date = addingDays(now, 1, timeZone);
  let days = 1;
  for (;;) {
    const weekday = civil(date, timeZone).weekday; // 0 = Sunday, 6 = Saturday
    if (weekday !== 6 && weekday !== 0) break;
    date = addingDays(date, 1, timeZone);
    days += 1;
  }
  const at = atHour(morningHour, date, timeZone);
  return { kind: days === 1 ? 'tomorrowMorning' : 'nextMondayMorning', atMillis: at };
}

function todayAt(hour: number, now: number, timeZone: string | undefined): number {
  return atHour(hour, now, timeZone);
}

function atHour(hour: number, date: number, timeZone: string | undefined): number {
  const c = civil(date, timeZone);
  return epochForCivil(c.year, c.month, c.day, hour, 0, 0, timeZone);
}

/**
 * How a FUTURE instant should be named: "today", "tomorrow", a weekday, or a date.
 *
 * `RelativeTime` cannot answer this. It is built for timestamps in the past — a future one
 * falls through its clock-skew branch and comes back as "hoy" whatever day it is, which for a
 * scheduled message is not a rounding error but a wrong promise on screen.
 */
export type DayKind = 'today' | 'tomorrow' | 'withinAWeek' | 'later';

export function dayKind(atMillis: number, now: number, timeZone?: string): DayKind {
  // Whole CALENDAR days from now to `atMillis` — not "how many 24-hour periods", which
  // disagrees with what a person means by "tomorrow" for most of every day.
  const days = calendarDaysBetween(now, atMillis, timeZone);
  if (days < 1) return 'today';
  if (days === 1) return 'tomorrow';
  if (days <= 6) return 'withinAWeek';
  return 'later';
}

function isSpanish(locale: string): boolean {
  return locale.toLowerCase().startsWith('es');
}

/** "lun" — the short weekday, for a day inside the coming week. */
export function weekdayLabel(atMillis: number, timeZone?: string, locale: string = uiLocale()): string {
  return dateFormatter(locale, { weekday: 'short' }, timeZone)
    .format(new Date(atMillis))
    .replaceAll('.', '')
    .toLocaleLowerCase(locale);
}

/**
 * "21 sep" — day and month, for anything further out than a week. Built from parts in the
 * Apple pattern's order ("d MMM" in Spanish, "MMM d" otherwise) rather than Intl's own layout,
 * which may add a "de" or reorder.
 */
export function dayMonthLabel(atMillis: number, timeZone?: string, locale: string = uiLocale()): string {
  const parts = dateFormatter(locale, { day: 'numeric', month: 'short' }, timeZone).formatToParts(new Date(atMillis));
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const text = isSpanish(locale) ? `${day} ${month}` : `${month} ${day}`;
  return text.replaceAll('.', '').toLocaleLowerCase(locale);
}

/**
 * The instant a date and a time-of-day chosen in the picker refer to. Combining them anywhere
 * but here is how a scheduled message lands six hours out for somebody in another country.
 * `day` is any instant inside the picked day (what a date picker hands back); only the reader's
 * own calendar says which day that is.
 */
export function instant(day: number, hour: number, minute: number, timeZone?: string): number {
  const c = civil(day, timeZone);
  const resolved = epochForCivil(c.year, c.month, c.day, hour, minute, 0, timeZone);
  // Rounded to the server's sweep like every other time here: a picker that let somebody
  // choose 09:03 would be showing a promise the server keeps at 09:05.
  return atServerSweep(resolved);
}
